import { Routes, Route, Navigate } from 'react-router-dom';
import { BottomNav } from '@/components/BottomNav';
import { useAuth } from '@/providers/AuthProvider';
import { useTelegram } from '@/providers/TelegramProvider';
import { Spinner } from '@/components/Spinner';
import { Empty } from '@/components/Empty';
import { AlphaPage } from '@/pages/AlphaPage';
import { BetaPage } from '@/pages/BetaPage';
import { BetaIndexPage } from '@/pages/BetaIndexPage';
import { Arbt1Page } from '@/pages/Arbt1Page';
import { GammaPage } from '@/pages/GammaPage';
import { DeltaPage } from '@/pages/DeltaPage';
import { EpsilonPage } from '@/pages/EpsilonPage';
import { ZetaPage } from '@/pages/ZetaPage';
import { EtaPage } from '@/pages/EtaPage';
import { ThetaPage } from '@/pages/ThetaPage';
import { EtaAlertDetailPage } from '@/pages/EtaAlertDetailPage';
import { SettingsPage } from '@/pages/SettingsPage';

const ALLOWED_TG_ID = Number(import.meta.env.VITE_ALLOWED_TELEGRAM_USER_ID ?? '234634696');

/** Главный компонент приложения: маршрутизация и нижнее меню. */
export function App(): JSX.Element {
  const { isLoading, error } = useAuth();
  const { webApp, isReady } = useTelegram();

  if (!isReady || isLoading) return <Spinner />;

  // Если приложение открыто через Telegram — проверяем, что пользователь разрешён.
  if (webApp) {
    const tgUserId = webApp.initDataUnsafe.user?.id;
    if (tgUserId !== ALLOWED_TG_ID) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100dvh',
            gap: 16,
            padding: 32,
            textAlign: 'center',
          }}
        >
          <span style={{ fontSize: 48 }}>🚫</span>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--tg-text)' }}>Доступ запрещён</h2>
          <p style={{ margin: 0, fontSize: 15, color: 'var(--tg-hint)' }}>
            Это приложение доступно только авторизованным пользователям.
          </p>
        </div>
      );
    }
  }

  if (error) {
    return (
      <Empty
        icon="⚠️"
        title="Не удалось войти"
        hint={`${error}. Откройте приложение через Telegram.`}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Navigate to="/alpha" replace />} />
        <Route path="/alpha" element={<AlphaPage />} />
        <Route path="/bot" element={<Navigate to="/beta/shp1" replace />} />
        <Route path="/beta" element={<BetaIndexPage />} />
        <Route path="/beta/shp1" element={<BetaPage />} />
        <Route path="/beta/arbt1" element={<Arbt1Page />} />
        <Route path="/gamma" element={<GammaPage />} />
        <Route path="/delta" element={<DeltaPage />} />
        <Route path="/epsilon" element={<EpsilonPage />} />
        <Route path="/zeta" element={<ZetaPage />} />
        <Route path="/eta" element={<EtaPage />} />
        <Route path="/theta" element={<ThetaPage />} />
        <Route path="/eta/:id" element={<EtaAlertDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/alpha" replace />} />
      </Routes>
      <BottomNav />
    </>
  );
}
